
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Divider, Dialog, Fade } from '@mui/material';
import { 
    Home as HomeIcon, 
    Create as CreateIcon, 
    EmojiObjects as EmojiObjectsIcon, 
    Equalizer as EqualizerIcon, 
    Group as GroupIcon,
    AccountCircle,
    Search as SearchIcon,
    Assignment as AssignmentIcon,
} from '@material-ui/icons';
import useStyles from '../../../assets/styles/TeacherDashboardStyles';
import TeacherDashboardLayout from './components/TeacherDashboardLayout';

const TeacherDashboard = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [showGuidance, setShowGuidance] = useState(location.state?.publishedProgram || false);

    const handleOpenDialog = () => setIsDialogOpen(true);
    const handleCloseDialog = () => setIsDialogOpen(false);
    
    const handleContinue = () => {
        handleCloseDialog();
        history.push({
            pathname: '/dq_pro/program/creation/preview',
            state: { teacherDash: true }
        });
    };

    const topCards = [
        {
            icon: <SearchIcon className={classes.cardIcon} style={{ color: '#0097b2', fontSize: '60px' }} />,
            title: 'Baseline',
            description: "Create an assessment to check students' Al-digital literacy skills.",
            btnText: 'Create',
            btnClass: classes.baselineBtn,
            onClick: handleOpenDialog
        },
        {
            icon: <AssignmentIcon className={classes.cardIcon} style={{ color: '#f54814', fontSize: '60px' }} />,
            title: 'Create Program',
            description: 'Design an Al-digital literacy program.',
            btnText: 'Create',
            btnClass: classes.createProgramBtn,
            onClick: handleOpenDialog
        }
    ];

    const insightStats = [
        { 
            value: '64%', 
            label: 'of students around the world are at risk for video gaming disorder? What about your students?',
            btnText: 'Check Now',
            variant: 'contained'
        },
        { 
            value: '27%', 
            label: 'of your students ages 8 from ABC class DO NOT know how to use Al safely',
            btnText: 'Create Program',
            variant: 'contained'
        },
        { 
            value: '22%', 
            label: 'of your students have NOT completed Al Ethics Program',
            btnText: 'View Progress',
            variant: 'outline'
        }
    ];

    const overviewStats = [
        { label: '# of Students Completed a Program', value: '2,123' },
        { label: '# of Competencies Students have Learned', value: '7,436' },
        { label: '% of Students Improving a Competency', value: '95%' },
        { label: '% of Students Intending to Change', value: '74%' },
    ];

    return (
        <TeacherDashboardLayout classes={classes} activeTab="home" headerTitle="Your Dashboard">
            <Box className={classes.mainContentInterior}>
                <Box className={classes.topRow}>
                    {topCards.map((card, idx) => (
                        <Box key={idx} className={classes.card}>
                            {card.icon}
                            <Typography className={classes.cardTitle} style={{ color: card.title === 'Create Program' ? '#f54814' : '#0097b2' }}>{card.title}</Typography>
                            <Typography className={classes.cardDesc}>{card.description}</Typography>
                            <Button className={card.btnClass} onClick={card.onClick}>{card.btnText}</Button>
                        </Box>
                    ))}

                    <Box className={classes.insightsCard}>
                        <Typography className={classes.insightsTitle}>Your Daily Insights</Typography>
                        <Box className={classes.insightsGrid}>
                            {insightStats.map((stat, idx) => (
                                <Box key={idx} className={classes.insightItem}>
                                    <Box>
                                        <Typography className={classes.insightValue}>{stat.value}</Typography>
                                        <Typography className={classes.insightLabel}>{stat.label}</Typography>
                                    </Box>
                                    <Button 
                                        className={classes.insightBtn}
                                    >
                                        {stat.btnText}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Divider className={classes.sectionDivider} />

                <Box className={classes.overviewSection}>
                    <Typography className={classes.overviewTitle}>Overall Overview</Typography>
                    <Box className={classes.overviewGrid}>
                        <Box className={classes.readinessCard}>
                            <Typography className={classes.readinessLabel}>AI Readiness Score</Typography>
                            <Typography className={classes.readinessScore}>75.20</Typography>
                            <Typography className={classes.readinessDescription}>
                                Above Average
                                <br />
                                <span className={classes.readinessSubText}>(vs Global Average)</span>
                            </Typography>

                        </Box>
                        {overviewStats.map((stat, idx) => (
                            <Box key={idx} className={classes.statCard}>
                                <Typography className={classes.statLabel}>{stat.label}</Typography>
                                <Typography className={classes.statValue}>{stat.value}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Button className={classes.viewByProgramBtn}>View Data By Program</Button>
                </Box>

                {/* Create Program Continuation Dialog */}
                <Dialog 
                    open={isDialogOpen} 
                    onClose={handleCloseDialog}
                    classes={{ paper: classes.dialogPaper }}
                    BackdropProps={{
                        style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' }
                    }}
                >
                    <Box className={classes.dialogContent}>
                        <Typography className={classes.dialogText}>
                            You started creating a program called<br/>
                            <span className={classes.dialogHighlight}>“AI Ethics for 5th Graders”</span>,<br/>
                            would you like to continue building off of that or start a new one?
                        </Typography>
                        <Box className={classes.dialogActions}>
                            <Button className={classes.dialogBtnStartOver} onClick={handleCloseDialog}>
                                Start Over
                            </Button>
                            <Button className={classes.dialogBtnContinue} onClick={handleContinue}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Dialog>
            </Box>

            {/* Guidance Card for Linking Program */}
            <Fade in={showGuidance} timeout={1000}>
                <Box className={classes.guidanceCard} onClick={() => setShowGuidance(false)}>
                    <Typography className={classes.guidanceText}>
                        Great! Your program is now ready to be<br/>
                        linked to your students.<br/><br/>
                        Let’s link the program.<br/><br/>
                        Click <span className={classes.guidanceHighlight}>“Your Classes”</span> on the left side<br/>
                        navigation screen of your dashboard.
                    </Typography>
                </Box>
            </Fade>
        </TeacherDashboardLayout>
    );
};

export default TeacherDashboard;
